import React from 'react';
import { Box, Typography, Button, IconButton, Select, MenuItem, FormControlLabel, Checkbox, TextField, Grid } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { useFormContext } from '../context/FormContext';
import { fileToBase64 } from '../utils/fileUtils';
import {
    RELATION_NASAL_FLOOR_OPTIONS,
    RELATION_SINUS_OPTIONS,
    RELATION_NASOPALATINE_OPTIONS
} from './ImpactionConstants';
import MmInput from './MmInput';
import {
    generateAdjacentTeethText,
    generateAnatomicalText,
    generateDepthText
} from '../utils/impactionTextGenerator';

// --- Local Options for Sentence Builders (from imp13.html) ---

const SUBJECT_PARTS = [
    'Tip of the crown', 'Crown', 'Cervical third of the root', 'Middle third of the root', 'Apical third of the root'
];

const RELATIONS = [
    'Not in contact with', 'In contact with', 'Overlapping'
];

const OBJECT_PARTS = [
    'Root', 'Crown', 'Buccal root', 'Palatal root', 'Fused root'
];

const POSITIONS = [
    'Mesially', 'Distally', 'Buccally', 'Palatally', 'Superiorly', 'Inferiorly'
];

const EFFECTS = [
    'No changes',
    'Surface root resorption',
    'Root resorption with associated dental caries',
    'Root resorption with associated PDL widening',
    'Root resorption with associated loss of lamina dura'
];

const DEPTH_OPTIONS = [
    'Superior: Crown near alveolar crest',
    'Moderate: Crown midway between crest and nasal floor',
    'Deep: Crown close to or contacting nasal floor/maxillary sinus'
];

// Helper for labels
const FieldLabel = ({ children }) => (
    <Typography sx={{ fontSize: '13px', mb: '5px', fontWeight: 'bold' }}>
        {children}
    </Typography>
);

const TextPreviewBox = ({ text }) => {
    if (!text) return null;
    return (
        <Box sx={{ mt: 2, p: 1.5, bgcolor: '#e3f2fd', borderLeft: '4px solid #2196f3', borderRadius: '0 4px 4px 0' }}>
            <Typography variant="body2" sx={{ fontStyle: 'italic', color: '#1565c0' }}>
                {text}
            </Typography>
        </Box>
    );
};

const ImpactionTab_Adjacent_Maxilla = ({ selectedTooth }) => {
    const { state, actions } = useFormContext();
    const toothData = state.impactionData?.[selectedTooth] || {};
    const adjacentData = toothData.adjacent || {};

    // Generate Text
    const adjText = generateAdjacentTeethText({ ...adjacentData, tooth: selectedTooth });
    const anatText = generateAnatomicalText(adjacentData);
    const depthText = generateDepthText(adjacentData);

    const updateData = (field, value) => {
        actions.setImpactionData({
            ...state.impactionData,
            [selectedTooth]: {
                ...toothData,
                adjacent: {
                    ...adjacentData,
                    [field]: value
                }
            }
        });
    };

    const handleImageUpload = async (key) => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.onchange = async (e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            try {
                const base64 = await fileToBase64(file);
                updateData(key, { base64, name: file.name, type: file.type });
            } catch (error) {
                console.error("Error converting file to Base64:", error);
            }
        };
        input.click();
    };

    const handleRemoveImage = (key) => { updateData(key, null); };

    const renderImageSection = (label, key, fileData, height = '150px') => {
        const preview = fileData ? fileData.base64 : null;
        return (
            <Box sx={{ width: '100%', mb: 2 }}>
                <Box sx={{ position: 'relative', mb: 1 }}>
                    <Box sx={{ width: '100%', height: height, border: '2px solid black', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.200', position: 'relative', overflow: 'hidden' }}>
                        {preview && (
                            <IconButton size="small" color="error" onClick={() => handleRemoveImage(key)} sx={{ position: 'absolute', top: 8, right: 8, bgcolor: 'background.paper', zIndex: 1 }}>
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        )}
                        {preview ? <img src={preview} alt={label} style={{ width: '100%', height: '100%', objectFit: 'contain' }} /> : <Typography variant="body2" color="text.secondary">{label}</Typography>}
                    </Box>
                </Box>
                <Button variant={preview ? "outlined" : "contained"} fullWidth color={preview ? "secondary" : "primary"} onClick={() => handleImageUpload(key)} size="small">
                    {preview ? 'Replace' : 'Upload'}
                </Button>
            </Box>
        );
    };

    const sectionBoxStyle = { border: '1px solid #e0e0e0', borderRadius: '4px', mb: '20px', p: '20px' };

    // reusable row for "Relation of impacted canine with X"
    const renderToothRelationRow = (targetName, dataPrefix) => {
        return (
            <Box sx={{ mb: 2, p: 1.5, border: '1px dashed #ccc', bgcolor: 'grey.50' }}>
                <Typography sx={{ color: '#d00', fontWeight: 'bold', fontSize: '14px', mb: 1 }}>
                    Relation with {targetName}
                </Typography>
                <Grid container spacing={1}>
                    <Grid item xs={12} sm={6} md={4}>
                        <FieldLabel>Part of Canine</FieldLabel>
                        <Select value={adjacentData[`${dataPrefix}Subject`] || ''} onChange={(e) => updateData(`${dataPrefix}Subject`, e.target.value)} displayEmpty size="small" fullWidth sx={{ bgcolor: 'white' }}>
                            {SUBJECT_PARTS.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                        </Select>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <FieldLabel>Relation</FieldLabel>
                        <Select value={adjacentData[`${dataPrefix}Relation`] || ''} onChange={(e) => updateData(`${dataPrefix}Relation`, e.target.value)} displayEmpty size="small" fullWidth sx={{ bgcolor: 'white' }}>
                            {RELATIONS.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                        </Select>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <FieldLabel>Target Part</FieldLabel>
                        <Select value={adjacentData[`${dataPrefix}Object`] || ''} onChange={(e) => updateData(`${dataPrefix}Object`, e.target.value)} displayEmpty size="small" fullWidth sx={{ bgcolor: 'white' }}>
                            {OBJECT_PARTS.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                        </Select>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <FieldLabel>Positioned</FieldLabel>
                        <Select value={adjacentData[`${dataPrefix}Position`] || ''} onChange={(e) => updateData(`${dataPrefix}Position`, e.target.value)} displayEmpty size="small" fullWidth sx={{ bgcolor: 'white' }}>
                            {POSITIONS.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                        </Select>
                    </Grid>
                    <Grid item xs={12} sm={12} md={8}>
                        <FieldLabel>Effect/Consequence</FieldLabel>
                        <Select value={adjacentData[`${dataPrefix}Effect`] || ''} onChange={(e) => updateData(`${dataPrefix}Effect`, e.target.value)} displayEmpty size="small" fullWidth sx={{ bgcolor: 'white' }}>
                            {EFFECTS.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                        </Select>
                    </Grid>
                </Grid>
            </Box>
        );
    };

    return (
        <Box>
            <Typography variant="h6" gutterBottom color="primary">
                Adjacent Structures & Anatomical Relationships (Maxilla {selectedTooth})
            </Typography>

            {/* SECTION 1: ADJACENT TEETH */}
            <Box sx={sectionBoxStyle}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>RELATIONSHIP WITH ADJACENT TEETH</Typography>
                {renderToothRelationRow('Tooth 11 (Central Incisor)', 'rel11')}
                {renderToothRelationRow('Tooth 12 (Lateral Incisor)', 'rel12')}
                {renderToothRelationRow('Tooth 14 (First Premolar)', 'rel14')}
                {renderToothRelationRow('Tooth 15 (Second Premolar)', 'rel15')}

                <Box sx={{ mt: 2 }}>
                    <FieldLabel>Relation with Deciduous 53</FieldLabel>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Enter description..."
                        value={adjacentData.rel53 || ''}
                        onChange={(e) => updateData('rel53', e.target.value)}
                    />
                </Box>
                <TextPreviewBox text={adjText} />
            </Box>

            {/* SECTION 2: ANATOMICAL STRUCTURES */}
            <Box sx={sectionBoxStyle}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>ANATOMICAL STRUCTURES</Typography>

                <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                        <FieldLabel>Nasopalatine Canal</FieldLabel>
                        <Box display="flex" flexDirection="column" gap={1}>
                            <Select value={adjacentData.relNasopalatine || ''} onChange={(e) => updateData('relNasopalatine', e.target.value)} displayEmpty size="small" fullWidth>
                                {RELATION_NASOPALATINE_OPTIONS.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                            </Select>
                            <Box display="flex" alignItems="center" gap={1}>
                                <Typography variant="caption">Bone barrier:</Typography>
                                <MmInput value={adjacentData.relNasopalatineMm} onChange={(e) => updateData('relNasopalatineMm', e.target.value)} width="110px" />
                            </Box>
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <FieldLabel>Nasal Floor</FieldLabel>
                        <Box display="flex" flexDirection="column" gap={1}>
                            <Select value={adjacentData.relNasalFloor || ''} onChange={(e) => updateData('relNasalFloor', e.target.value)} displayEmpty size="small" fullWidth>
                                {RELATION_NASAL_FLOOR_OPTIONS.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                            </Select>
                            <Box display="flex" alignItems="center" gap={1}>
                                <Typography variant="caption">Bone barrier:</Typography>
                                <MmInput value={adjacentData.relNasalFloorMm} onChange={(e) => updateData('relNasalFloorMm', e.target.value)} width="110px" />
                            </Box>
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <FieldLabel>Maxillary Sinus</FieldLabel>
                        <Box display="flex" flexDirection="column" gap={1}>
                            <Select value={adjacentData.relSinus || ''} onChange={(e) => updateData('relSinus', e.target.value)} displayEmpty size="small" fullWidth>
                                {RELATION_SINUS_OPTIONS.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                            </Select>
                            <Box display="flex" alignItems="center" gap={1}>
                                <Typography variant="caption">Bone barrier:</Typography>
                                <MmInput value={adjacentData.relSinusMm} onChange={(e) => updateData('relSinusMm', e.target.value)} width="110px" />
                            </Box>
                        </Box>
                    </Grid>
                </Grid>

                <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                    <Box sx={{ width: '300px' }}>
                        {renderImageSection('Anatomical Relation Image', 'anatomicalImage', adjacentData.anatomicalImage)}
                    </Box>
                </Box>
                <TextPreviewBox text={anatText} />
            </Box>

            {/* SECTION 3: DEPTH & IMPRESSION */}
            <Box sx={sectionBoxStyle}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>DEPTH OF IMPACTION</Typography>
                <Grid container spacing={2}>
                    {DEPTH_OPTIONS.map(opt => (
                        <Grid item xs={12} key={opt}>
                            <FormControlLabel
                                control={<Checkbox checked={adjacentData.depth === opt} onChange={() => updateData('depth', opt)} />}
                                label={opt}
                            />
                        </Grid>
                    ))}
                </Grid>
                <TextPreviewBox text={depthText} />
            </Box>

            <Box sx={sectionBoxStyle}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>RADIOGRAPHIC IMPRESSION</Typography>
                <TextField
                    fullWidth
                    multiline
                    rows={4}
                    placeholder="Enter radiographic impression..."
                    value={adjacentData.impression || ''}
                    onChange={(e) => updateData('impression', e.target.value)}
                />
            </Box>

        </Box>
    );
};

export default ImpactionTab_Adjacent_Maxilla;
